import {
  mapState,
  mapMutations,
  mapActions,
  mapGetters,
} from 'vuex';
import {
  mdiPrinter,
} from '@mdi/js';
import {
  amountFormat,
  amountToNumber,
  getItemsIndexByID,
  focusAmount,
} from '../../util/shared/vue-global';
import {
  isNotBeforeDate,
  defaultDurationOfCorrection,
} from '../../util/shared/tmc-global';
import { fields } from './DepositVoucher.shared';

const sumReduce = (acc, val) => acc + (amountToNumber(val.amount) || 0);
const reconcillationMapper = [
  { depositType: 'CASHCHECK', collectionName: 'cashCheckItems' },
  { depositType: 'CREDIT', collectionName: 'creditItems' },
  // trust is the independent isTrust flag, not a depositType. This entry only
  // lets cleanupUnrelatedDetails wipe trustItems when isTrust is unchecked;
  // 'TRUST' is a sentinel that never matches a real depositType.
  { depositType: 'TRUST', collectionName: 'trustItems' },
  { depositType: 'EFT', collectionName: 'EFTItems' },
];
export default {
  data: () => ({
    icons: {
      mdiPrinter,
    },
    registrationOverride: false,
  }),
  created() {
    if (this.internalValue.type === 'DEPOSIT'
      && this.unregisteredDep && this.isNewItem) {
      this.internalValue.type = 'UNREGISTERED';
    }
    this.$store.dispatch('SystemConfig/loadConfig', { requiredScope: 'odconfig' });
  },
  computed: {
    ...mapGetters('user', ['getSettings']),
    ...mapState({
      departments: (state) => {
        if (state.OD.newEditIndex === -1) {
          return state.OD.departments.filter((dept) => dept.isNewEntries !== true);
        }
        return state.OD.departments;
      },
      transTypes: (state) => state.enums.ODTransType,
      statuses: (state) => state.enums.ODStatus,
      nextVoucherNumber: (state) => state.OD.nextVoucherNumber,
      nextRegistrationNumber: (state) => state.OD.nextRegistrationNumber,
      deptBalanceBefore: (state) => state.OD.deptBalanceBefore,
      newEditIndex: (state) => state.OD.newEditIndex,
      voucherRegPolling: (state) => state.OD.voucherRegPolling,
      voucherRegPulse: (state) => state.OD.voucherRegPulse,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
      transactions: (state) => state.OD.transactions,
      autoFocus: (state) => state.OD.autoFocus,
      stateDialog: (state) => state.OD.newEditDialog,
    }),
    ...mapGetters([
      'todaysDate',
    ]),
    isTransactionTypeReadOnly() {
      // if (this.isNewItem) {
      //   return false;
      // }
      // For now we are making type readonly for new as well as edit dialog box
      return true;
    },
    voucherRegPulseClass() {
      return this.voucherRegPulse ? 'color-anim-pulse' : 'color-anim-black';
    },
    unregisteredDep() {
      return this && this.odSettingItem && this.odSettingItem.unregisteredDep;
    },
    durationOfCorrection() {
      if (this && this.odSettingItem && this.odSettingItem.durationOfCorrection) {
        return parseInt(this.odSettingItem.durationOfCorrection, 10)
          || defaultDurationOfCorrection;
      }
      return defaultDurationOfCorrection;
    },
    filteredTransTypes() {
      if (!this.transTypes) {
        return [];
      }
      return this.transTypes.filter((t) => (
        (t.name !== 'UNREGISTERED' || this.unregisteredDep)
        && (t.name !== 'DEPOSIT' || !this.isNewItem || !this.unregisteredDep)
      ));
    },
    reqdSelectValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
    departmentLabel() {
      const balance = (this.deptBalanceBefore !== undefined)
        ? amountFormat(this.deptBalanceBefore) : '...';
      // console.log(`stateBal: ${this.deptBalanceBefore}, amount: ${amount}, balance: ${balance}`);
      return `Department (bal before: $${balance})`;
    },
    unregisteredOrDeposit() {
      return this.internalValue.type === 'DEPOSIT'
        || this.internalValue.type === 'UNREGISTERED';
    },
    isTrustFlag() {
      return !!this.internalValue.isTrust;
    },
    isTrustReceipt() {
      return this.unregisteredOrDeposit && this.isTrustFlag;
    },
    voidVoucher() {
      return this.internalValue.type === 'VOUCHER'
        && this.internalValue.status === 'VOID';
    },
    isBegBal() {
      return this.internalValue.type === 'BEGINNINGBALANCE';
    },
    shouldValidateItemizedAmount() {
      return this.unregisteredOrDeposit && this.isDepositItemsRequiredValidation;
    },
    registerDateRules() {
      return [
        (value) => {
          const isNotBefore = isNotBeforeDate(
            value, this.todaysDate, (this.durationOfCorrection + 1),
          );
          return isNotBefore || 'You are not allowed to change or enter transactions that'
            + ` are more than ${this.durationOfCorrection} business days old. Please use a correction`
            + ' transaction type for today\'s date instead.';
        },
      ];
    },
    fiscalYearValidation() {
      return [
        (value) => !!value || 'Required',
        (value) => !Number.isNaN(parseFloat(value)) || 'Numeric required',
        (value) => parseFloat(value) > 0 || 'Must be a positive year',
        (value) => parseFloat(value) < 3000 || 'Must be a reasonable year',
      ];
    },
    amountValidation() {
      return [
        (value) => {
          // type number with value 0 is 'falsy'
          // interpret it as 'truthy'
          if (amountToNumber(value) === 0) {
            return true;
          }
          return !!value || 'Required';
        },
        (value) => {
          if (!this.voidVoucher) {
            return true;
          }
          return amountToNumber(value) === 0 || 'Voided vouchers must have zero amounts.';
        },
        (value) => {
          if (!this.unregisteredOrDeposit) {
            return true;
          }
          if (!this.shouldValidateItemizedAmount) {
            return true;
          }
          const totalAsNumber = this.totalReconcillationAmount();
          return amountToNumber(value) === totalAsNumber || 'Mismatched Reconciliation Amount';
        },
        (value) => {
          if (this.unregisteredOrDeposit) {
            return amountToNumber(value) >= 0 || 'You are not allowed to enter negative deposits.'
              + ' Please use a correction transaction type.';
          }
          return true;
        },
      ];
    },
    correctedAmountValidation() {
      return [
        (value) => {
          if (!this.internalValue.isCorrected) {
            return true;
          }
          if (amountToNumber(value) === 0) {
            return true;
          }
          return !!value || 'Required';
        },
      ];
    },
    // true only once the SERVER-confirmed record already has isCorrected - not just
    // because the checkbox is currently ticked in an unsaved edit. That's the only
    // state where a correction transaction is guaranteed to already exist, so
    // unchecking is a guaranteed-to-be-rejected no-op we can head off client-side.
    correctionAlreadyExists() {
      const serverTrans = (this.transactions || []).find((t) => t.id === this.editedItem.id);
      return !!(serverTrans && serverTrans.isCorrected);
    },
    isNewItem() {
      return this.newEditIndex === -1;
    },
    shouldSaveTrans() {
      const saveTrans = isNotBeforeDate(
        this.internalValue.register,
        this.todaysDate,
        (this.durationOfCorrection + 1),
      );
      return this.isNewItem || saveTrans;
    },
    isTooltipVisible() {
      return this.odSettingItem && this.odSettingItem.registrationNumber;
    },
    registrationHoverCaption() {
      if (this.isNewItem) {
        return 'Record must be saved before its registration number can be changed!';
      }
      return 'Double-click to override (use ⚠️ caution)!';
    },
    isDepositItemsRequiredValidation() {
      let requiredValidation = false;
      reconcillationMapper.forEach((collection) => {
        if (this.internalValue.depositType === collection.depositType
          && (this.internalValue[collection.collectionName] || []).length > 0
        ) {
          requiredValidation = true;
        }
      });
      if (this.internalValue.depositType === 'CASHCHECK'
          && this.odSettingItem.cashCheckReconciliation) {
        requiredValidation = true;
      }
      return requiredValidation;
    },
    containerClass() {
      return this.getSettings.lightdark === 'light' ? 'grey lighten-5' : '';
    },
  },
  methods: {
    ...mapActions('OD', [
      'loadFiscalYears',
    ]),
    ...mapMutations('OD', [
      'setNewEditItem',
      'setNewEditDialog',
      'setNewEditIndex',
      'setVoucherRegPolling',
      'setDefaultStats',
      'setPreWhatStr',
      'setPreWhomStr',
      'setAutoFocus',
    ]),
    amountFormat,
    getItemsIndexByID,
    focusAmount,
    registerDateBlurred(value) {
      const isNotBeforeTwoPrior = isNotBeforeDate(
        value, this.todaysDate, (this.durationOfCorrection + 1),
      );
      const valueDt = new Date(value);
      const todayDt = new Date(this.todaysDate);
      const diffMs = todayDt - valueDt;
      if (isNotBeforeTwoPrior && diffMs > 0) {
        this.$store.dispatch('flashInfo', `Please remember to export data for the GL for ${value}
        as this change will affect GL balances for ${value}.`);
      }
    },
    updateDeptAndNextTransNum() {
      this.updateDeptBalance();
      this.determineNextTransactionNumbers();
    },
    updateDeptBalance() {
      // trustType options are filtered by department — a stale selection from the
      // previous department would otherwise silently save a mismatched pair
      this.editedItem.trustType = undefined;
      this.$store.dispatch('OD/loadDeptBalance', this.editedItem);
      if (this.editedItem.type === 'VOUCHER') {
        this.determineNextTransactionNumbers(true);
        this.$store.dispatch('OD/loadWhoms', {
          fiscalYear: this.editedItem.fiscalYear,
        });
        this.$store.dispatch('OD/loadWhats', {
          fiscalYear: this.editedItem.fiscalYear,
        });
      }
    },
    determineNextTransactionNumbers(isRequired = false) {
      if (this.editedItem.isEftCode) {
        return;
      }
      if (this.isNewItem || isRequired) {
        const loadStatsPromise = this.$store.dispatch(
          'OD/loadTransStats',
          {
            type: this.editedItem.type,
            department: this.editedItem.department,
            fiscalYear: this.editedItem.fiscalYear,
          },
        );
        loadStatsPromise.then(() => {
          if (this.editedItem.isEftCode) {
            return;
          }
          // Voucher # is department-scoped: refresh it for new items and whenever the
          // department/FY changes (isRequired) so it reflects the selected department,
          // not the previous one. Guard on department since the stat is dept-keyed.
          if (this.editedItem.department) {
            this.internalValue.voucher = this.nextVoucherNumber;
          }
          // Registration # is type-scoped (not dept); only auto-assign for new items.
          if (this.isNewItem) {
            this.internalValue.registration = this.nextRegistrationNumber;
          }
        });
      }
    },
    defaultItem() {
      const defaultObj = { ...fields() };
      return defaultObj;
    },
    close() {
      this.setDefaultStats();
      this.setNewEditDialog(false);
      this.setVoucherRegPolling(undefined);
      this.$nextTick(() => {
        this.setNewEditItem(this.defaultItem());
        this.setNewEditIndex(-1);
      });
    },
    validateForm() {
      const result = this.$refs.form.validate();
      if (!result && global.isVueUnitTest) {
        // Do not call validate() per input here — it can re-enter Vuetify and stall the
        // test runner for a very long time. Log field class names after the single form validate.
        const inputs = (this.$refs.form && this.$refs.form.inputs)
          ? Array.from(this.$refs.form.inputs)
          : [];
        const errorClassLists = inputs.map((i) => (
          i && i.$el && i.$el.className ? String(i.$el.className) : '?'
        ));
        console.warn(JSON.stringify(errorClassLists, null, 1));
      }
      return result;
    },
    saveableEdits() {
      this.editedItem.amount = amountToNumber(this.editedItem.amount);
      this.editedItem.correctedAmount = this.editedItem.isCorrected
        ? amountToNumber(this.editedItem.correctedAmount) : null;
      // validation-ish concerns (parsing string input back to numbers, etc)
      if (this.editedItem.finalized === '' || this.editedItem.status === 'OUTSTANDING') {
        this.editedItem.finalized = null;
      }
      // Trust checkbox unchecked - discard the scalar trust-only fields so
      // nothing stale survives the save. trustItems itself is handled below
      // (it's a "collection", cleared via cleanupUnrelatedDetails).
      if (!this.editedItem.isTrust) {
        this.editedItem.trustType = null;
        this.editedItem.isPrePaidMobileHome = false;
        this.editedItem.depositAddress = null;
      }
      // don't store unrelated details. trustItems is independent of depositType
      // now (isTrust is its own flag), so it's never in the "unrelated" set to
      // wipe while isTrust is checked.
      const isTrustItemsCollection = (c) => c.collectionName === 'trustItems';
      const hadDepositType = !!this.editedItem.depositType;
      if (!hadDepositType) {
        this.editedItem.depositType = null;
      }
      // keep a collection if it matches the (possibly just-nulled) depositType,
      // or if it's trustItems and isTrust is checked - everything else is
      // "unrelated details" and gets wiped.
      const shouldKeepCollection = (c) => c.depositType === this.editedItem.depositType
        || (this.editedItem.isTrust && isTrustItemsCollection(c));
      const collectionToUpdate = reconcillationMapper.filter((c) => !shouldKeepCollection(c));
      this.cleanupUnrelatedDetails(collectionToUpdate);
      if (hadDepositType) {
        this.updateDetailsForReq();
      }
      // ------------------
      // misc warnings
      // ------------------
      let shouldValidateForm = true;

      if (this.editedItem.type === 'VOUCHER') {
        let newBalance = (this.deptBalanceBefore || 0.0) - amountToNumber(this.editedItem.amount);
        if (!this.isNewItem) {
          const trans = this.transactions.find((t) => t.id === this.editedItem.id);
          if (trans) {
            newBalance += amountToNumber(trans.amount);
          }
        }
        if (newBalance < 0) {
          const deptDescription = this.departments
            .find(
              (dept) => dept.id === this.editedItem.department,
            ).caption;
          if (!window.confirm('Warning: This voucher will result in a negative balance'
            + ` for "${deptDescription}". Do you wish to proceed?`)) {
            shouldValidateForm = false;
          }
        }
      }
      return shouldValidateForm ? this.validateForm() : false;
    },
    cleanupUnrelatedDetails(collectionToUpdate) {
      collectionToUpdate.forEach((collection) => {
        this.editedItem[collection.collectionName] = [];
      });
    },
    updateDetailsForReq() {
      const collection = reconcillationMapper
        .find((m) => m.depositType === this.editedItem.depositType);
      if (collection) {
        this.editedItem[collection.collectionName] = (this.editedItem[collection.collectionName]
            || []).map((item) => {
          const amount = amountToNumber(item.amount);
          return {
            ...item,
            amount,
          };
        });
      }
    },
    async imagesLeftClick(leftClick) {
      if (this.shouldSaveTrans) {
        const didSave = await this.internalSave(undefined, false);
        if (didSave) {
          this.$nextTick(() => {
            leftClick();
          });
        }
      } else {
        leftClick();
      }
    },
    async imagesRightClick(rightClick) {
      if (this.shouldSaveTrans) {
        const didSave = await this.internalSave(undefined, false);
        if (didSave) {
          rightClick();
        }
      } else {
        rightClick();
      }
    },
    // saveableEdits() nulls correctedAmount as soon as isCorrected is unchecked, so the
    // field goes blank on screen even if the server then rejects the save (e.g. a
    // correction already exists). Put the last-synced values back so a blocked uncheck
    // doesn't look like the corrected amount got wiped out.
    revertCorrectedFieldsOnFailure() {
      const serverTrans = (this.transactions || []).find((t) => t.id === this.editedItem.id);
      if (serverTrans) {
        this.editedItem.isCorrected = serverTrans.isCorrected;
        this.editedItem.correctedAmount = serverTrans.correctedAmount;
      }
    },
    async internalSave(e, autoClose) {
      const valResults = this.saveableEdits();
      let upsResult = false;
      // console.log(valResults);
      if (valResults) {
        // keyboard event shift + enter
        if (e && e.shiftKey) {
          upsResult = await this.$store.dispatch('OD/upsTransaction', {
            transaction: this.editedItem,
          });

          if (upsResult) {
            this.$store.dispatch('flashSuccess', 'Successfully saved transaction');
            // Reset items ID for next.
            this.editedItem.id = '';
            this.determineNextTransactionNumbers();
            this.$refs.amount.focus();
          } else {
            this.revertCorrectedFieldsOnFailure();
            this.$store.dispatch('flashError', 'Failed to save transaction');
          }
        } else {
          // Stop poling when going for saving transaction
          this.setNewEditIndex(-2);
          // upsert it
          upsResult = await this.$store.dispatch('OD/upsTransaction', {
            transaction: this.editedItem,
          });
          if (!upsResult) {
            this.revertCorrectedFieldsOnFailure();
          }
          this.setNewEditIndex(this.getItemsIndexByID(this.transactions, this.editedItem));
          if (autoClose && upsResult) {
            this.close();
          }
        }
        this.loadFiscalYears();
        if (upsResult) {
          this.syncTrustTypeLastNumberUsed();
        }
      }
      return upsResult;
    },
    syncTrustTypeLastNumberUsed() {
      const { trustType, trustItems } = this.editedItem;
      if (!trustType || !(trustItems || []).length) {
        return;
      }
      const highestToNumber = trustItems.reduce(
        (max, item) => (item.toNumber > max ? item.toNumber : max), 0,
      );
      if (!highestToNumber) {
        return;
      }
      // server applies this as an atomic $max, so a stale client-side comparison
      // here is just an optimization to skip the round-trip, not a correctness guard
      const trustTypeRecord = (this.trustTypes || []).find((t) => t._id === trustType);
      if (!trustTypeRecord || highestToNumber > (trustTypeRecord.lastNumberUsed || 0)) {
        this.bumpTrustTypeLastNumberUsed({ id: trustType, lastNumberUsed: highestToNumber });
      }
    },
    saveToStore() {
      if (this.isNewItem && this.editedItem.type === 'VOUCHER') {
        this.setPreWhatStr(this.editedItem.whatStr);
        this.setPreWhomStr(this.editedItem.whomStr);
      }
      if (this.isNewItem && this.editedItem.type === 'DEPOSIT') {
        localStorage.setItem('odtrans.usedLastRegister', this.editedItem.register);
      }
    },
    async save(e) {
      this.saveToStore();
      return this.internalSave(e, true);
    },
    roundAmount() {
      this.internalValue.amount = amountFormat(amountToNumber(this.internalValue.amount));
    },
    amountToNumber() {
      this.internalValue.amount = amountToNumber(this.internalValue.amount);
    },
    roundCorrectedAmount() {
      this.internalValue.correctedAmount = amountFormat(
        amountToNumber(this.internalValue.correctedAmount),
      );
    },
    correctedAmountToNumber() {
      this.internalValue.correctedAmount = amountToNumber(this.internalValue.correctedAmount);
    },
    onCorrectedChange(value) {
      if (!value && this.correctionAlreadyExists) {
        // Vuetify's checkbox already flips its native DOM state before this handler
        // runs - reverting the bound value in the same tick leaves it visually
        // unchecked even though the data is back to true. Defer to nextTick so it
        // repaints.
        this.$nextTick(() => {
          this.internalValue.isCorrected = true;
        });
        this.$store.dispatch('flashError', 'Cannot uncheck Corrected - a correction'
          + ' transaction already exists. Reverse the correction transaction instead.');
      }
    },
    overrideRegistrationNumber() {
      if (this.isTooltipVisible) {
        this.registrationOverride = true;
      }
    },
    totalReconcillationAmount() {
      const nameOfCollection = (reconcillationMapper
        .find((m) => m.depositType === this.internalValue.depositType) || {}).collectionName;
      const items = this.internalValue[nameOfCollection] || [];
      const total = items.reduce(sumReduce, 0);
      return amountToNumber(total);
    },
  },
};
